import { useEffect } from 'react';

/**
 * Custom hook to dynamically update meta tags in the document head
 * This is used for Open Graph and Twitter Card tags for social media sharing
 *
 * @param {Object} metaTags - Object containing meta tag configurations
 * @param {string} metaTags.title - Page title
 * @param {string} metaTags.description - Page description
 * @param {string} metaTags.image - Social sharing image URL
 * @param {string} metaTags.url - Canonical page URL
 * @param {string} metaTags.type - OG type (website, article, profile, etc.)
 *
 * @example
 * useMetaTags({
 *   title: "Senior Developer at Google",
 *   description: "Join our team...",
 *   image: "https://example.com/logo.png",
 *   url: "https://example.com/job/123",
 *   type: "article"
 * });
 */
const useMetaTags = (metaTags) => {
  useEffect(() => {
    if (!metaTags || typeof metaTags !== 'object') {
      return;
    }

    const { title, description, image, url, type = 'website' } = metaTags;

    /**
     * Helper function to update or create a meta tag
     * @param {string} attribute - 'name' or 'property'
     * @param {string} key - The attribute value (e.g., 'og:title')
     * @param {string} content - The content value
     */
    const updateMetaTag = (attribute, key, content) => {
      if (!content) return;

      let element = document.querySelector(`meta[${attribute}="${key}"]`);

      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }

      element.setAttribute('content', content);
    };

    // Update Open Graph tags
    if (title) {
      updateMetaTag('property', 'og:title', title);
      updateMetaTag('name', 'title', title);
    }

    if (description) {
      updateMetaTag('property', 'og:description', description);
      updateMetaTag('name', 'description', description);
    }

    if (image) {
      updateMetaTag('property', 'og:image', image);
    }

    if (url) {
      updateMetaTag('property', 'og:url', url);
    }

    if (type) {
      updateMetaTag('property', 'og:type', type);
    }

    // Update Twitter Card tags
    if (title) {
      updateMetaTag('name', 'twitter:title', title);
    }

    if (description) {
      updateMetaTag('name', 'twitter:description', description);
    }

    if (image) {
      updateMetaTag('name', 'twitter:image', image);
    }

    if (url) {
      updateMetaTag('name', 'twitter:url', url);
    }

    // Only log if we have actual data to update
    if (title || description || image || url) {
      console.log('[useMetaTags] Meta tags updated:', { title, description, image, url, type });
    }

    // Cleanup function (optional - meta tags typically persist)
    return () => {
      // Meta tags are intentionally left in place for navigation
      // They will be updated by the next page that uses this hook
    };
  }, [metaTags]);
};

export default useMetaTags;
